import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, BadgeCheck, CheckCircle2, Eye, Landmark, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import {
  useAddRateSetMutation,
  useCalculatePayRunMutation,
  useCreatePayRunMutation,
  usePayRunActionMutation,
  usePayRunQuery,
  usePayRunsQuery,
  useRateSetsQuery,
} from '@/client/api/hr/payroll';
import { PAY_RUN_BADGE, type PayRun, type Payslip, type StatutoryRateSet } from '@/client/types/hr';
import { AccountingPageShell } from './components/AccountingPageShell';
import { Field, inputClass } from './components/spend-ui';
import { DeleteConfirmDialog, EmptyState, Panel, RowMenu, StatusBadge } from '../HumanResource/components/hr-ui';
import { PayslipView } from '../HumanResource/components/PayslipView';
import { money, totals } from '../HumanResource/components/comp-format';
import { formatDay } from '../HumanResource/components/leave-format';
import { useEmployees } from '../HumanResource/hr-store';

const monthName = (p: string) => new Date(`${p}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
const SKIP_TEXT = { no_pay_recorded: 'no pay recorded', hourly_pay: 'paid hourly (enter hours by hand)' };

function RunDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const runQ = usePayRunQuery(id);
  const employees = useEmployees();
  const calc = useCalculatePayRunMutation();
  const act = usePayRunActionMutation();
  const canManage = useGate(PERMISSIONS.payroll.manage).allowed;
  const [viewing, setViewing] = useState<Payslip | null>(null);
  const [pending, setPending] = useState<'approve' | 'pay' | 'delete' | null>(null);
  const [skipped, setSkipped] = useState<PayRun['skipped']>();
  const byId = useMemo(() => new Map(employees.map((e) => [e.id, e])), [employees]);
  const run = runQ.data;

  if (runQ.isLoading) return <EmptyState label="Loading pay run…" />;
  if (runQ.isError || !run) return <EmptyState label={`Could not load the pay run: ${(runQ.error as Error)?.message ?? ''}`} />;
  const slips = [...(run.payslips ?? [])].sort((a, b) => (byId.get(a.employeeId)?.name ?? '').localeCompare(byId.get(b.employeeId)?.name ?? ''));
  const flagged = slips.filter((s) => s.flags.length);

  const recalc = () =>
    calc.mutate(run.id, {
      onSuccess: (r) => {
        setSkipped(r.skipped);
        toast.success(`Recalculated: ${r.payslips?.length ?? 0} payslip(s)`);
      },
      onError: (e) => toast.error((e as Error).message),
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" /> All pay runs
        </button>
        {canManage && (
          <div className="flex gap-2">
            {run.status === 'draft' && (
              <>
                <Button variant="outline" className="gap-2" onClick={recalc} disabled={calc.isPending}>
                  <RefreshCw className="h-4 w-4" /> Recalculate
                </Button>
                <Button variant="outline" className="gap-2 text-red-600" onClick={() => setPending('delete')}>
                  <Trash2 className="h-4 w-4" /> Delete draft
                </Button>
                <Button className="gap-2" onClick={() => setPending('approve')}>
                  <CheckCircle2 className="h-4 w-4" /> Approve
                </Button>
              </>
            )}
            {run.status === 'approved' && (
              <Button className="gap-2" onClick={() => setPending('pay')}>
                <BadgeCheck className="h-4 w-4" /> Mark paid
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold text-gray-900">{monthName(run.period)}</h2>
        <StatusBadge label={run.status} className={`capitalize ${PAY_RUN_BADGE[run.status]}`} />
        <span className="text-sm text-gray-500">
          {run.payDate ? `Pay date ${formatDay(run.payDate)}` : 'No pay date set'}
          {run.calculatedAt && ` · calculated ${formatDay(run.calculatedAt)}`}
          {run.paidAt && ` · paid ${formatDay(run.paidAt)}`}
        </span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(run.totals ?? []).flatMap((t) => [
          ['People', String(t.people)],
          [`Gross (${t.currency})`, money(t.gross, t.currency)],
          [`Net to pay (${t.currency})`, money(t.net, t.currency)],
          [`Total cost (${t.currency})`, money(t.employerCost, t.currency)],
        ]).map(([label, value], i) => (
          <div key={i} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">{label}</p>
            <p className="mt-1 text-xl font-semibold text-gray-900">{value}</p>
          </div>
        ))}
      </div>

      {(flagged.length > 0 || (skipped && skipped.length > 0)) && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="mb-1 flex items-center gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4" /> Check before approving
          </p>
          <ul className="list-disc pl-5">
            {flagged.map((s) => (
              <li key={s.id}>{byId.get(s.employeeId)?.name}: no tax rules for {s.currency}; deductions not worked out.</li>
            ))}
            {(skipped ?? []).map((s) => (
              <li key={s.employeeId}>
                {byId.get(s.employeeId)?.name ?? 'Someone'} left out: {SKIP_TEXT[s.reason]}.
              </li>
            ))}
          </ul>
        </div>
      )}

      {slips.length === 0 ? (
        <EmptyState label="No payslips. Record people's pay in People › Compensation, then recalculate." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Employee</th>
                <th className="px-4 py-3 text-right font-medium">Gross</th>
                <th className="px-4 py-3 text-right font-medium">SSNIT</th>
                <th className="px-4 py-3 text-right font-medium">PAYE + bonus tax</th>
                <th className="px-4 py-3 text-right font-medium">Net</th>
                <th className="px-4 py-3 text-right font-medium">Employer cost</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {slips.map((s) => {
                const d = (code: string) => s.lines.deductions.filter((x) => x.code === code).reduce((n, x) => n + x.amount, 0);
                return (
                  <tr key={s.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setViewing(s)}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {byId.get(s.employeeId)?.name ?? '—'}
                      {s.flags.length > 0 && <AlertTriangle className="ml-1.5 inline h-3.5 w-3.5 text-amber-600" />}
                    </td>
                    <td className="px-4 py-3 text-right">{money(s.gross, s.currency)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{money(d('ssnit_employee'), s.currency)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{money(d('paye') + d('bonus_tax'), s.currency)}</td>
                    <td className="px-4 py-3 text-right font-medium">{money(s.net, s.currency)}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{money(s.employerCost, s.currency)}</td>
                    <td className="px-4 py-3 text-right">
                      <RowMenu items={[{ label: 'View payslip', icon: Eye, onSelect: () => setViewing(s) }]} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Payslip</DialogTitle>
          </DialogHeader>
          {viewing && <PayslipView slip={{ ...viewing, payDate: run.payDate }} name={byId.get(viewing.employeeId)?.name ?? 'Employee'} period={run.period} />}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!pending}
        onOpenChange={(o) => !o && setPending(null)}
        title={pending === 'approve' ? `Approve ${monthName(run.period)} payroll?` : pending === 'pay' ? 'Mark this run paid?' : 'Delete this draft run?'}
        description={
          pending === 'approve'
            ? `${totals((run.totals ?? []).map((t) => ({ amount: t.net, currency: t.currency })))} net to ${slips.length} people. It is frozen once approved, and everyone sees their payslip in My workplace. Someone other than whoever last calculated it must approve.`
            : pending === 'pay'
              ? 'Do this once the money has gone out. Bonuses in this run are marked paid.'
              : 'Its payslips are deleted too. You can start the month again.'
        }
        actionLabel={pending === 'approve' ? 'Approve' : pending === 'pay' ? 'Mark paid' : 'Delete'}
        danger={pending === 'delete'}
        onConfirm={() => {
          if (pending)
            act.mutate(
              { id: run.id, action: pending },
              {
                onSuccess: () => {
                  toast.success(pending === 'approve' ? 'Approved' : pending === 'pay' ? 'Marked paid' : 'Deleted');
                  if (pending === 'delete') onBack();
                },
                onError: (e) => toast.error((e as Error).message),
              },
            );
          setPending(null);
        }}
      />
    </div>
  );
}

function RatesPanel() {
  const ratesQ = useRateSetsQuery();
  const add = useAddRateSetMutation();
  const canManage = useGate(PERMISSIONS.payroll.manage).allowed;
  const [draft, setDraft] = useState<(Omit<StatutoryRateSet, 'id'> & { bandsText: string }) | null>(null);
  const sets = ratesQ.data ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const inForce = sets.find((s) => s.effectiveFrom <= today);

  const openNew = () => {
    const base = inForce ?? sets[0];
    if (!base) return;
    setDraft({
      country: base.country,
      effectiveFrom: today,
      source: '',
      rates: base.rates,
      bandsText: base.rates.paye_bands.map(([w, p]) => `${w ?? 'rest'} @ ${p}%`).join('\n'),
    });
  };

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    const bands: [number | null, number][] = [];
    for (const line of draft.bandsText.split('\n').map((l) => l.trim()).filter(Boolean)) {
      const m = line.match(/^(rest|[\d.]+)\s*@\s*([\d.]+)\s*%?$/i);
      if (!m) {
        toast.error(`Could not read band "${line}". Use "588 @ 0%" and end with "rest @ 35%".`);
        return;
      }
      bands.push([m[1].toLowerCase() === 'rest' ? null : Number(m[1]), Number(m[2])]);
    }
    const { bandsText: _ignored, ...body } = draft;
    add.mutate(
      { ...body, rates: { ...draft.rates, paye_bands: bands } },
      {
        onSuccess: () => {
          toast.success('Rate set added');
          setDraft(null);
        },
        onError: (err) => toast.error((err as Error).message),
      },
    );
  };

  return (
    <Panel
      title="Tax and SSNIT rates"
      action={
        <PermissionButton code={PERMISSIONS.payroll.manage} size="sm" variant="outline" className="gap-1.5" onClick={openNew}>
          <Plus className="h-3.5 w-3.5" /> New rate set
        </PermissionButton>
      }
    >
      {ratesQ.isLoading ? (
        <p className="text-sm text-gray-500">Loading rates…</p>
      ) : (
        <div className="space-y-3">
          {sets.map((s) => (
            <div key={s.id} className="rounded-lg border border-gray-100 p-3 text-sm">
              <div className="mb-1 flex items-center justify-between">
                <p className="font-medium text-gray-900">
                  {s.country} · from {formatDay(s.effectiveFrom)}
                  {s.id === inForce?.id && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">in force</span>}
                </p>
                <span className="text-xs text-gray-400">{s.source}</span>
              </div>
              <p className="text-xs text-gray-600">
                PAYE ({s.rates.currency}/month): {s.rates.paye_bands.map(([w, p]) => `${w ?? 'rest'} @ ${p}%`).join(', ')}
              </p>
              <p className="text-xs text-gray-600">
                SSNIT {s.rates.ssnit_employee_pct}% employee / {s.rates.ssnit_employer_pct}% employer, capped at {s.rates.ssnit_cap_monthly.toLocaleString()} · bonus{' '}
                {s.rates.bonus_flat_pct}% up to {s.rates.bonus_cap_pct_of_annual_basic}% of annual basic
              </p>
            </div>
          ))}
          <p className="text-xs text-gray-500">Rate sets are never edited: a correction is a new set from a date, so past payslips keep their meaning. Have an accountant check these before the first real run.</p>
        </div>
      )}

      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New rate set</DialogTitle>
          </DialogHeader>
          {draft && canManage && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="In force from">
                  <input type="date" required value={draft.effectiveFrom} onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Source (law, notice, link)">
                  <input value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })} className={inputClass} />
                </Field>
              </div>
              <Field label="PAYE monthly bands, one per line, lowest first">
                <textarea
                  rows={7}
                  value={draft.bandsText}
                  onChange={(e) => setDraft({ ...draft, bandsText: e.target.value })}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 font-mono text-sm outline-none ring-blue-500 focus:ring-2"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ['ssnit_employee_pct', 'SSNIT employee %'],
                    ['ssnit_employer_pct', 'SSNIT employer %'],
                    ['ssnit_cap_monthly', 'SSNIT cap per month'],
                    ['bonus_flat_pct', 'Bonus tax %'],
                    ['bonus_cap_pct_of_annual_basic', 'Bonus allowance, % of annual basic'],
                  ] as const
                ).map(([key, label]) => (
                  <Field key={key} label={label}>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={draft.rates[key]}
                      onChange={(e) => setDraft({ ...draft, rates: { ...draft.rates, [key]: Number(e.target.value) } })}
                      className={inputClass}
                    />
                  </Field>
                ))}
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={add.isPending}>
                  Add rate set
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

export default function PayRunsPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get('run');
  const runsQ = usePayRunsQuery();
  const create = useCreatePayRunMutation();
  const [form, setForm] = useState<{ period: string; payDate: string } | null>(null);
  const runs = runsQ.data ?? [];
  const latestPaid = runs.find((r) => r.status === 'paid');

  const stats = [
    { key: 'runs', label: 'Pay runs', value: runs.length },
    { key: 'waiting', label: 'Waiting for approval', value: runs.filter((r) => r.status === 'draft').length },
    { key: 'topay', label: 'Approved, to pay', value: runs.filter((r) => r.status === 'approved').length },
    { key: 'last', label: 'Last paid', value: latestPaid ? monthName(latestPaid.period) : '—' },
  ];

  return (
    <AccountingPageShell
      mock={false}
      title="Payroll"
      description="One run per month, worked out from approved pay, approved bonuses and unpaid leave, with PAYE and SSNIT. Calculated and recorded here; the money goes out by bank or MoMo."
      crumbs={[{ label: 'Accounting', href: '/admin/accounting' }, { label: 'Payroll' }, { label: 'Pay runs' }]}
      icon={Landmark}
      actions={
        <PermissionButton
          code={PERMISSIONS.payroll.manage}
          className="gap-2"
          onClick={() => {
            const next = runs[0] ? new Date(`${runs[0].period}-01T00:00:00Z`) : new Date();
            if (runs[0]) next.setUTCMonth(next.getUTCMonth() + 1);
            setForm({ period: next.toISOString().slice(0, 7), payDate: '' });
          }}
        >
          <Plus className="h-4 w-4" /> New pay run
        </PermissionButton>
      }
      stats={stats}
    >
      {selected ? (
        <RunDetail id={selected} onBack={() => setParams({})} />
      ) : runsQ.isError ? (
        <EmptyState label={`Could not load pay runs: ${(runsQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : runsQ.isLoading ? (
        <EmptyState label="Loading pay runs…" />
      ) : (
        <div className="space-y-4">
          {runs.length === 0 ? (
            <EmptyState label="No pay runs yet. Record everyone's pay in People › Compensation, then start a run." />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Month</th>
                    <th className="px-4 py-3 font-medium">People</th>
                    <th className="px-4 py-3 font-medium">Net to pay</th>
                    <th className="px-4 py-3 font-medium">Total cost</th>
                    <th className="px-4 py-3 font-medium">Pay date</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {runs.map((r) => (
                    <tr key={r.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setParams({ run: r.id })}>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {monthName(r.period)}
                        {!!r.flagged && <AlertTriangle className="ml-1.5 inline h-3.5 w-3.5 text-amber-600" />}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{(r.totals ?? []).reduce((n, t) => n + t.people, 0)}</td>
                      <td className="px-4 py-3 text-gray-900">{totals((r.totals ?? []).map((t) => ({ amount: t.net, currency: t.currency })))}</td>
                      <td className="px-4 py-3 text-gray-600">{totals((r.totals ?? []).map((t) => ({ amount: t.employerCost, currency: t.currency })))}</td>
                      <td className="px-4 py-3 text-gray-600">{r.payDate ? formatDay(r.payDate) : '—'}</td>
                      <td className="px-4 py-3">
                        <StatusBadge label={r.status} className={`capitalize ${PAY_RUN_BADGE[r.status]}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <RatesPanel />
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New pay run</DialogTitle>
          </DialogHeader>
          {form && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                create.mutate(
                  { period: form.period, payDate: form.payDate || null },
                  {
                    onSuccess: (r) => {
                      toast.success(`${monthName(r.period)}: ${r.payslips?.length ?? 0} payslip(s) worked out`);
                      setForm(null);
                      setParams({ run: r.id });
                    },
                    onError: (err) => toast.error((err as Error).message),
                  },
                );
              }}
            >
              <Field label="Month">
                <input type="month" required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} className={inputClass} />
              </Field>
              <Field label="Pay date (optional)">
                <input type="date" value={form.payDate} onChange={(e) => setForm({ ...form, payDate: e.target.value })} className={inputClass} />
              </Field>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setForm(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? 'Working it out…' : 'Create and calculate'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AccountingPageShell>
  );
}
